import { useMemo } from "preact/hooks";
import { useProjectDetailPage } from "../store";
import {
  Box, Typography, Divider, List, ListItem, ListItemText,
  ListItemIcon, IconButton, Avatar, Card, CardContent, Tooltip
} from "@mui/material";
import { styled } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import AttachmentIcon from '@mui/icons-material/Attachment';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: 8,
  overflow: "hidden",
}));

const fileType = (name) => {
  const ext = String(name || "").toLowerCase().split(".").pop();
  if (["jpg","jpeg","png","gif","webp"].includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  return "document";
};
const fileIcon = (name) => {
  const t = fileType(name);
  if (t === "image") return <ImageIcon />;
  if (t === "pdf") return <PictureAsPdfIcon />;
  return <DescriptionIcon />;
};
const fileAvatarSx = (theme, name) => {
  const t = fileType(name);
  if (t === "image") return { bgcolor: theme.palette.success.main, color: theme.palette.success.contrastText };
  if (t === "pdf")   return { bgcolor: theme.palette.error.main,   color: theme.palette.error.contrastText };
  return { bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText };
};

export function CardAttachments() {
  const { project, role } = useProjectDetailPage();

  const attachments = useMemo(
    () => (Array.isArray(project?.attachments) ? project.attachments : []),
    [project]
  );

  const handleDownload = (att) => {
    try {
      const baseUrl = import.meta.env.DEV ? "http://localhost:3000" : window.location.origin;
      const quotationId = project?.quotationId;
      if (!quotationId) return;

      const endpoint = `/${role}/api/quotations/${quotationId}/download/${encodeURIComponent(att.name)}`;

      const sessionId = localStorage.getItem("sessionId");
      const form = document.createElement("form");
      form.method = "GET";
      form.action = baseUrl + endpoint;
      form.style.display = "none";

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "sessionId";
      input.value = sessionId || "";
      form.appendChild(input);

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch {}
  };

  return (
    <StyledCard elevation={1}>
      <Box sx={{ 
          py: 1.5, 
          px: 2, 
          bgcolor: "primary.main",
          color: "primary.contrastText",
        }}>
        <Typography variant="h6" color="inherit" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachmentIcon />
            Archivos Adjuntos ({attachments.length})
        </Typography>
      </Box>
      <CardContent>
        {!attachments.length ? (
          <Typography variant="body2" color="text.secondary">
            Este proyecto no tiene adjuntos.
          </Typography>
        ) : (
          <List dense disablePadding>
            {attachments.map((att) => {
              const id = att?._id || att?.name;
              const sizeStr = att?.size ? `${(att.size / 1024 / 1024).toFixed(2)} MB` : "archivo";

              return (
                <ListItem
                  key={id}
                  secondaryAction={
                    <Tooltip title="Descargar">
                      <IconButton edge="end" size="small" onClick={() => handleDownload(att)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                  sx={{ borderRadius: 1, px: 1, "&:hover": { bgcolor: "action.hover" } }}
                >
                  <ListItemIcon sx={{ minWidth: 44 }}>
                    <Avatar sx={(theme) => ({ width: 32, height: 32, ...fileAvatarSx(theme, att?.name) })}>
                      {fileIcon(att?.name)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: "caption" }}
                    primary={att?.name || "(Sin nombre)"}
                    secondary={sizeStr}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </CardContent>
    </StyledCard>
  );
}